import { type ProcessUploadResponse, useApiInspectionsBaleLoadedUpdate } from 'api';
import { logger } from 'monitor';
import { useCallback, useMemo } from 'react';

import { type Bale, PhotoRejectionReason } from '../../types';
import { compressBalePhoto } from '../../utils/compressBalePhoto';
import { createBaleFromFile } from '../../utils/createBaleFromFile';
import { isFileEmpty } from '../../utils/files/isFileEmpty';
import { isAspectRatioValid } from '../../utils/isAspectRatioValid';
import { isBaleDuplicated } from '../../utils/isBaleDuplicated';
import { isFileTypeValid } from '../../utils/isFileTypeValid';
import { trackBaleSopOutcome } from '../../utils/trackBaleSopOutcome';
import { useUploadBale } from '../useUploadBale/useUploadBale';
import { useBaleAnalysisStore } from './useBaleAnalysisStore';

export const useBaleAnalysis = () => {
  const { mutateAsync: uploadBale } = useUploadBale();
  const { mutate: setLoadingStatus } = useApiInspectionsBaleLoadedUpdate();
  const {
    bales,
    actions: { addBale, updateBale, removeBale, setBales },
  } = useBaleAnalysisStore();

  const isBaleLoading = useMemo(() => bales.some((bale) => bale.isLoading), [bales]);
  const isPhotoRejected = useMemo(() => bales.some((bale) => bale.analysisStatus === 'rejected-photo'), [bales]);
  const isMaterialRejected = useMemo(
    () => bales.some((bale) => bale.analysisStatus === 'rejected-material' && bale.loadingStatus === undefined),
    [bales]
  );

  const validateBale = useCallback(
    async (bale: Bale): Promise<Bale> => {
      if (isFileEmpty(bale.photo)) {
        logger.warn('empty file detected', bale);
        return {
          ...bale,
          analysisStatus: 'rejected-photo',
          photoRejectedReason: PhotoRejectionReason.EMPTY_FILE,
        };
      }

      if (!isFileTypeValid(bale.photo)) {
        logger.warn('invalid file type detected', bale);
        return {
          ...bale,
          analysisStatus: 'rejected-photo',
          photoRejectedReason: PhotoRejectionReason.INVALID_FORMAT,
        };
      }

      if (await isBaleDuplicated(bales, bale)) {
        logger.warn('duplicate photo detected', bale);
        return {
          ...bale,
          analysisStatus: 'rejected-photo',
          photoRejectedReason: PhotoRejectionReason.DUPLICATE,
        };
      }

      if (!(await isAspectRatioValid(bale.photo))) {
        logger.warn('invalid aspect ratio detected', bale);
        return {
          ...bale,
          analysisStatus: 'rejected-photo',
          photoRejectedReason: PhotoRejectionReason.BAD_ASPECT_RATIO,
        };
      }

      return bale;
    },
    [bales]
  );

  const addBalePhoto = useCallback(
    async ({ photo, model }: { photo: File; model?: string }): Promise<ProcessUploadResponse | null> => {
      logger.info('Adding bale photo', { photo });
      const processStartTime = performance.now();
      const bale = await validateBale(createBaleFromFile(photo));
      if (bale.analysisStatus === 'rejected-photo') {
        logger.info('Bale photo rejected', { bale, photoRejectedReason: bale.photoRejectedReason });
        trackBaleSopOutcome(bale, 'frontend');
        addBale(bale);
        return {
          ...bale,
          artifacts: [],
          attachment_details: null,
          created: false,
          image: {
            id: 0,
            url: URL.createObjectURL(bale.photo),
          },
          message: 'Rejected photo',
          model_results: {
            accepted: false,
            coco_prediction: [],
            grade: 'NA',
            model_name: String(model),
            model_version: '1.0',
            outputs: [
              {
                acceptance: false,
                features: {},
                highest_grade_name: 'Reject',
                image_rejected: true,
                image_rejection_reasons: [String(bale.photoRejectedReason)],
                reasons: [],
              },
            ],
          },
          target_weight_percentage: null,
        };
      }

      try {
        const compressedPhoto = await compressBalePhoto(photo);
        logger.info('Bale photo compressed', {
          compressedSize: compressedPhoto.size,
          originalSize: photo.size,
          percentage: Math.round((compressedPhoto.size / photo.size) * 100),
        });
        bale.photo = compressedPhoto;
        const result = await uploadBale({ bale, model, startTime: processStartTime });

        return result;
      } catch (error) {
        logger.error('Failed to process bale photo', error as Error);
        return null;
      }
    },
    [validateBale, addBale, uploadBale]
  );

  const retryAnalysis = useCallback(
    ({ baleId, model }: { baleId: string; model?: string }) => {
      const bale = bales.find((bale) => bale.id === baleId);
      if (!bale) {
        logger.error('Bale not found');
        return;
      }

      uploadBale({ bale, model, startTime: performance.now() });
    },
    [bales, uploadBale]
  );

  const updateLoadingStatus = useCallback(
    (baleId: string, loadingStatus: 'loaded' | 'not-loaded') => {
      const bale = bales.find((bale) => bale.id === baleId);
      if (!bale?.analysis?.image.id) {
        logger.error('image ID not found');
        return;
      }

      setLoadingStatus(
        {
          data: { loaded: loadingStatus === 'loaded' },
          id: Number(bale.analysis.image.id),
        },
        {
          onSuccess: () => updateBale({ ...bale, loadingStatus }),
        }
      );
    },
    [bales, setLoadingStatus, updateBale]
  );

  const resetBales = useCallback(() => {
    setBales([]);
  }, [setBales]);

  return {
    addBalePhoto,
    bales,
    isBaleLoading,
    isMaterialRejected,
    isPhotoRejected,
    removeBalePhoto: removeBale,
    resetBales,
    retryAnalysis,
    updateLoadingStatus,
  };
};
